import {
  HistoricalUtilisationQueryDto,
  KnownHistoricalUtilisationQueryDtoGroupBy
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useEffect, useMemo } from 'react';
import { useImmer } from 'use-immer';
import { useQueryListHubs } from '../../lockers/hubs.queries';
import { getDateRange, DateRange } from '../_shared/date-range';
import { useQueryLockerUtilisationReport } from './locker-utilisation-report.queries';

type State = { utilisationQuery: Omit<HistoricalUtilisationQueryDto, 'projectID'>; range: DateRange };

export function useLockerUtilisationReport(projectID: string) {
  const [state, setState] = useImmer<State>({
    utilisationQuery: {
      ...getDateRange(DateRange.Week),
      groupBy: KnownHistoricalUtilisationQueryDtoGroupBy.Size
    },
    range: DateRange.Week
  });

  const hubData = useQueryListHubs(projectID, { includeAppearsOn: true });
  const data = useQueryLockerUtilisationReport(projectID, { ...state.utilisationQuery, projectID });

  const hubOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [
      { label: 'All related hubs', value: 'all' },
      ...(hubData.data || []).map((hub) => ({ label: hub.name, value: hub.id }))
    ];
    return options;
  }, [hubData.data]);

  // Watch for changes to the hub data, and check if the current selected hub is still
  // a valid option. If not, reset to "all" hubs
  useEffect(() => {
    const currentHub = state.utilisationQuery.hubID;
    if (!currentHub || currentHub === 'all' || !hubData.data || hubData.isLoading) {
      return;
    }
    if (!hubData.data.map((hub) => hub.id).includes(currentHub)) {
      setState((draft) => {
        draft.utilisationQuery.hubID = undefined;
      });
    }
  }, [hubData.data, hubData.isLoading, setState, state.utilisationQuery.hubID]);

  const dateRangeOptions: { label: string; value: DateRange }[] = [
    { value: DateRange.Day, label: 'Today' },
    { value: DateRange.Week, label: '1 week' },
    { value: DateRange.Month, label: '1 month' },
    { value: DateRange.HalfYear, label: '6 months' },
    { value: DateRange.Year, label: '1 year' },
    { value: DateRange.AllTime, label: 'All time' }
  ];

  const groupByOptions: { label: string; value: KnownHistoricalUtilisationQueryDtoGroupBy }[] = [
    { value: KnownHistoricalUtilisationQueryDtoGroupBy.Size, label: 'Grouped by size' },
    { value: KnownHistoricalUtilisationQueryDtoGroupBy.Hub, label: 'Grouped by hub' },
    { value: KnownHistoricalUtilisationQueryDtoGroupBy.None, label: 'No grouping' }
  ];

  function onSelectHub(selection: string) {
    const selectedHubID = selection === 'all' ? undefined : selection;
    setState((draft) => {
      draft.utilisationQuery.hubID = selectedHubID;
    });
  }

  function onSelectDateRange(selection: DateRange) {
    setState((draft) => {
      const { fromDate, toDate } = getDateRange(selection);
      draft.utilisationQuery.fromDate = fromDate;
      draft.utilisationQuery.toDate = toDate;
      draft.range = selection;
    });
  }

  function onSelectGroupBy(groupBy: KnownHistoricalUtilisationQueryDtoGroupBy) {
    setState((draft) => {
      draft.utilisationQuery.groupBy = groupBy;
    });
  }

  return {
    state,
    data,
    options: { hubOptions, dateRangeOptions, groupByOptions },
    handlers: { onSelectDateRange, onSelectGroupBy, onSelectHub }
  } as const;
}
